/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-async-promise-executor */
/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Wed Mar 29 2023
 **/

import { useRef, useState, useEffect } from "react";
import { atom, useAtom } from "jotai";
import { useLocation } from "react-router-dom";
import { EventEmitter } from "eventemitter3";

import { Properties } from "@/frameworks/libraries/properties";
import { Configure } from "@/frameworks/libraries/utilities";

import { useAccount, useUsers, useSpace } from "@/frameworks/core/API";
import { onGeneralErrorHandler } from "@/frameworks/core/API";

export const GlobalReadyStore = atom<boolean>(false);

const LOGTAG = "#useServiceState#";

export default function useServiceState(callback?: Callback) {
	const [isReady, setReady] = useAtom(GlobalReadyStore);
	const location = useLocation();
	const { state } = location;
	const event = useRef(new EventEmitter());

	const accountAPI = useAccount({
		onSuccess(name) {
			switch (name) {
				case "generalRoles": {
					spaceAPI.search.allowTypes.execute();
					break;
				}
			}
		},
		onError(name, error) {
			if (!onGeneralErrorHandler(name, error)) {
				console.warn(LOGTAG, "useAccount has error", name, error);
			}
		}
	});

	const [userProperty, setUserProperty] = useState(null);
	const userAPI = useUsers({
		onSuccess(name, result) {
			switch (name) {
				case "me:inquiry": {
					if (result.data) {
						const me = {
							...result.data,
							id: result.data.userId,
							email: result.data.username,
							name: result.data.name || ""
						};
						setUserProperty(result.data);
						Configure.userID = result.data.userId;
					}
					userAPI.invitesList.execute();
					userAPI.relatedUsers.list.execute();
					spaceAPI.mySpaces.list.execute();
					break;
				}
			}
		},
		onError(name, error) {
			if (!onGeneralErrorHandler(name, error)) {
				console.warn("#useServiceState# useUsers has error", name, error);
			}
		}
	});
	const spaceAPI = useSpace({
		onSuccess(name, result) {
			switch (name) {
				case "search:allowTypes": {
					// 원래 me api 실행하던 곳, property 업데이트 문제로 group, space 관련 확인이 끝나면 실행하도록 변경
					if (state?.mode === "space") {
						// 로그인 + 스페이스 url 입력
						// 스페이스 가입 여부 판별 필요
						spaceAPI.detail.execute({ spaceId: state.id });
					}
					userAPI.me.inquiry.execute();
					break;
				}

				case "mySpaces:list": {
					setReady(true);
					callback?.();
					event.current.emit("ready");
					break;
				}

				case "detail": {
					// url로 입장한 경우 + 참여한 그룹 일때 그룹 라운지 info 세팅
					// group,space 관련 flow 끝으로 me 실행
					if (result?.data.spaceJoinStatus === "JOINED" || result?.data.spaceAccessAllowType === "PUBLIC") {
						// 스페이스로 이동시켜줌
						Properties.property.routerInfo = {
							spaceId: result?.data.spaceId,
							spaceScene: result?.data.spaceScene,
							spaceType: "SPACE",
							groupId: result?.data.groupId,
							groupName: result?.data.groupName,
							spaceName: result?.data.name
						};
					}
					userAPI.me.inquiry.execute();
					break;
				}
			}
		},
		onError(name, error) {
			// space 정보 가져오기 실패, 유효하지 않은 스페이스인 경우 그룹 관련 state 삭제
			switch (name) {
				case "detail": {
					location.state = null;
					userAPI.me.inquiry.execute();
				}
			}
			if (!onGeneralErrorHandler(name, error)) {
				console.warn("#useServiceState# useSpace has error", name, error);
			}
		}
	});

	const preparing = () => {
		return new Promise<boolean>(async (resolve) => {
			if (Configure.is_authenticated) {
				// 로그인 키가 있을 경우 로그인 상태입니다.
				console.debug(LOGTAG, "Preparing service");
				event.current.once("ready", () => {
					console.debug(LOGTAG, "Service prepared");
					resolve(true);
				});
				setReady(false);
				callback?.();
				accountAPI.generalRoles.execute();
			} else {
				resolve(false);
			}
		});
	};
	return {
		isReady,
		get hasToken() {
			return Configure.is_authenticated;
		},
		onGeneralErrorHandler,
		accounts: accountAPI,
		users: userAPI,
		spaces: spaceAPI,
		preparing,
		userProperty
	};
}
